import { Inject, Injectable, OnInit, Optional } from "@angular/core";
import { mergeMap as _observableMergeMap, catchError as _observableCatch, mergeMap, catchError } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";

import { API_BASE_URL } from "./service-shared";
import { environment } from "environments/environment";
import { blobToText, processProblemResponse } from "./service-functions";
import { Organisation } from "app/shared/model/organisation.model";
import { GetOrganisationListResponse, GetOrganisationResponse } from "./model/organisation-service.model";

@Injectable({ providedIn: 'root' })
export class OrganisationServiceProxy implements OnInit {
    private http: HttpClient;
    private baseUrl: string;
    private basePath: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.basePath = environment.config?.formServiceEndpoint !== undefined ? environment.config.formServiceEndpoint : '';
        this.baseUrl = (baseUrl ?? '') + this.basePath;
    }

    async ngOnInit() { }

    getOrganisation(organisationId: string, formId?: string, formVersion?: number): Observable<GetOrganisationResponse> {
        let url_ = (this.baseUrl + "/GetOrganisation").replace(/[?&]$/, "");

        let options_: any = {
            body: {
                organisationId: organisationId,
                formId: formId,
                formVersion: formVersion
            },
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_)
            .pipe(
                mergeMap((response_: any) => {
                    return this.processGetOrganisation(response_);
                }),
                catchError((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetOrganisation(response_ as any);
                        } catch (e) {
                            return throwError(() => new Error(e));
                        }
                    }

                    return throwError(() => new Error(response_));
                }));
    }

    protected processGetOrganisation(response: HttpResponseBase): Observable<GetOrganisationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetOrganisationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetOrganisationResponse>(null as any);
    }

    getBaseOrganisation(): Observable<void | GetOrganisationResponse> {
        let url_ = this.baseUrl + "/GetBaseOrganisation";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({})
        };

        return this.http.request("post", url_, options_).pipe(
            mergeMap((response_: any) => {
                return this.processGetBaseOrganisation(response_);
            }),
            catchError((response_: any) => {
                if (response_ instanceof HttpResponseBase) {
                    try {
                        return this.processGetBaseOrganisation(response_ as any);
                    } catch (e) {
                        return throwError(() => new Error(e)) as any as Observable<void>;
                    }
                } else
                    return throwError(() => new Error(response_)) as any as Observable<void>;
            }));
    }

    protected processGetBaseOrganisation(response: HttpResponseBase): Observable<GetOrganisationResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetOrganisationResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetOrganisationResponse>(null as any);
    }

    getOrganisationList(): Observable<void | GetOrganisationListResponse> {
        let url_ = this.baseUrl + "/GetOrganisationList";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetOrganisationList(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetOrganisationList(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processGetOrganisationList(response: HttpResponseBase): Observable<GetOrganisationListResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetOrganisationListResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<GetOrganisationListResponse>(null as any);
    }

    getOrganisationByUrl(url: string): Observable<void | Organisation> {
        let url_ = this.baseUrl + "/GetOrganisationByUrl";
        url_ = url_.replace(/[?&]$/, "");

        let options_: any = {
            body: { url: url },
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_: any) => {
            return this.processGetOrganisationByUrl(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetOrganisationByUrl(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<void>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<void>;
        }));
    }

    protected processGetOrganisationByUrl(response: HttpResponseBase): Observable<Organisation> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
                (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = Organisation.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
            ?? _observableOf<Organisation>(null as any);
    }
}
